import { ChangeDetectionStrategy, Component, OnInit, signal, ViewEncapsulation } from "@angular/core";
import { S25LoadingInlineStaticComponent } from "../../s25-loading/s25.loading.inline.static.component";
import { IngestionFormComponent } from "../../data-ingestion/ingestion.form.component";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { DataIngestionService, IngestionSubmission } from "../../data-ingestion/data.ingestion.service";

@TypeManagerDecorator("s25-ng-guest-registration-form")
@Component({
    selector: "s25-ng-guest-registration-form",
    template: `
        <div class="c-padding--double">
            @if (loading()) {
                <s25-ng-loading-inline-static [text]="'Loading registration form...'" />
            } @else if (error()) {
                <div class="error-message">
                    <h2>Registration form not found</h2>
                    <p>{{ error() }}</p>
                </div>
            } @else if (submission()) {
                <div class="registration-form-container">
                    <h1>Registration Form</h1>
                    <p class="registration-intro">Fill out this form to complete your registration</p>
                    <s25-ng-ingestion-form
                        [submissionIdUuid]="submission()!.submissionIdUuid!"
                        [ingestionPointId]="submission()!.ingestionPointId!"
                        [readOnly]="submission()!.submitted || false"
                        (onSubmit)="onFormSubmit()"
                    />
                </div>
            }
        </div>
    `,
    styles: `
        :host {
            display: block;
        }

        s25-ng-loading-inline-static,
        .error-message {
            text-align: center;
            padding: 3rem;
        }

        .error-message h2 {
            color: #d32f2f;
            margin-bottom: 1rem;
        }

        .registration-form-container {
            max-width: 800px;
            margin: 0 auto;
        }

        .registration-form-container h1 {
            margin-bottom: 1rem;
            color: #2573a7;
        }

        .registration-intro {
            margin-bottom: 2rem;
            font-size: 1.1rem;
            color: #666;
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [IngestionFormComponent, S25LoadingInlineStaticComponent, IngestionFormComponent],
})
export class SeGuestRegistrationFormComponent implements OnInit {
    readonly loading = signal(true);
    readonly error = signal<string | null>(null);
    readonly submission = signal<IngestionSubmission | null>(null);

    ngOnInit() {
        // Extract submissionIdUuid from URL hash (e.g., /#!/guest-registration-form/<uuid>)
        const hash = window.location.hash;
        const match = hash.match(/guest-registration-form\/([^\/]+)/);
        const submissionIdUuid = match ? match[1] : null;

        if (!submissionIdUuid) {
            this.error.set("Invalid registration form link. Missing submission identifier.");
            this.loading.set(false);
            return;
        }

        this.loadForm(submissionIdUuid);
    }

    async loadForm(submissionIdUuid: string) {
        try {
            this.loading.set(true);
            this.error.set(null);

            const submission = await DataIngestionService.getEventSubmission(submissionIdUuid);

            if (!submission) {
                this.error.set("Registration form not found. It may have been deleted or the link may be invalid.");
                return;
            }

            this.submission.set(submission);
        } catch (err: any) {
            console.error("Failed to load registration form:", err);
            this.error.set(err?.message || "Failed to load registration form. Please check your link and try again.");
        } finally {
            this.loading.set(false);
        }
    }

    onFormSubmit() {
        // Show success message and optionally redirect
        alert("Your registration form has been submitted.");
    }
}
